import { Component, Input, forwardRef, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskDirective } from 'ngx-mask';

@Component({
  selector: 'umkt-custom-input',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, NgxMaskDirective],
  templateUrl: './custom-input.component.html',
  styleUrls: ['./custom-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomInputComponent),
      multi: true,
    }
  ]
})
export class CustomInputComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder = '';
  @Input() type = 'text';
  @Input() disabled = false;
  @Input() id: string | undefined;
  @Input() name: string | undefined;
  @Input() mask: string | undefined;
  @Input() showClearButton: boolean = true;
  @Input() showPasswordToggle: boolean = true;
  @Input() showSearchIcon: boolean = false;
  @Input() error: boolean | undefined;
  @Input() maxlength?: number;
  @Input() floatingLabel: boolean = false;
  /** Quando true, permite apenas letras e números (A-Z, a-z, 0-9) */
  @Input() restrictToAlphanumeric = false;
  @Input() restrictToNumeric = false;
  @Input() restrictToDecimal = false;
  @Output() passwordToggle = new EventEmitter<boolean>();
  @Output() searchSubmit = new EventEmitter<string>();
  @Output() inputBlur = new EventEmitter<void>();

  value: string = '';
  onChange: (value: string) => void = () => {
    // ControlValueAccessor implementation - will be set by registerOnChange
  };
  onTouched: () => void = () => {
    // ControlValueAccessor implementation - will be set by registerOnTouched
  };

  isPasswordVisible = false;
  currentInputType: string;
  isFocused = false;

  constructor() {
    this.currentInputType = this.type;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) {
      this.currentInputType = this.type;
      if (this.type !== 'password') {
        this.isPasswordVisible = false; // Reset visibility if type changes from password
      }
    }
  }

  writeValue(value: any): void {
    this.value = value || '';
  }

  get hasValue(): boolean {
    if (!this.value) return false;
    const stringValue = this.value.toString().trim();
    // Remove mask characters that might be present even in empty state
    return stringValue.length > 0 && !/^[._\-\s()]+$/.test(stringValue);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  clearInput(): void {
    this.value = '';
    this.onChange('');
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
    this.currentInputType = this.isPasswordVisible ? 'text' : 'password';
    this.passwordToggle.emit(this.isPasswordVisible);
  }


  onPaste(event: ClipboardEvent): void {
    if (!this.restrictToAlphanumeric && !this.restrictToNumeric && !this.restrictToDecimal) return;
    event.preventDefault();
    let pasted = event.clipboardData?.getData('text') || '';
    if (this.restrictToDecimal) {
      pasted = pasted.replace(/[^\d.]/g, '');
      const firstDot = pasted.indexOf('.');
      if (firstDot !== -1) {
        pasted =
          pasted.slice(0, firstDot + 1) +
          pasted.slice(firstDot + 1).replace(/\./g, '');
      }
    } else if (this.restrictToNumeric) {
      pasted = pasted.replace(/\D/g, '');
    } else if (this.restrictToAlphanumeric) {
      pasted = pasted.replace(/[^a-zA-Z0-9]/g, '');
    }
    const max = this.maxlength ?? pasted.length;
    const sanitized = pasted.slice(0, max);
    this.value = sanitized;
    this.onChange(sanitized);
  }

  preventSpace(event: KeyboardEvent): void {
    if (this.type === 'password' && event.key === ' ') {
      event.preventDefault();
    }
    if (this.restrictToAlphanumeric && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (!/^[a-zA-Z0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    }
    if (this.restrictToNumeric && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    }
    if (this.restrictToDecimal && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const isDigit = /^\d$/.test(event.key);
      const isDot = event.key === '.';
      const alreadyHasDot = (this.value ?? '').toString().includes('.');
      if (!isDigit && !(isDot && !alreadyHasDot)) {
        event.preventDefault();
      }
    }
  }

  public setPasswordVisibility(isVisible: boolean): void {
    this.isPasswordVisible = isVisible;
    this.currentInputType = this.isPasswordVisible ? 'text' : 'password';
  }

  onSearchClick(): void {
    this.searchSubmit.emit(this.value);
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    this.searchSubmit.emit(this.value);
  }

  onInputFocus(): void {
    this.isFocused = true;
  }

  onInputBlur(): void {
    this.isFocused = false;
    this.onTouched();
    this.inputBlur.emit();
  }
}
